/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   5/31/21
 */

import React, {useState} from "react";
import {
	Badge, Box, Fade,
	IconButton, Link,
	Popover, Tooltip, Typography,
} from "@mui/material";
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import Const from "../const.json";
import {format} from "../dates-helper";

const LAST_SEEN_STORAGE_KEY = "vega.last_seen_version";

export default function UpdatesMenu() {

	const currentVersion = (process.env.REACT_APP_VERSION as string).split(".")[0];
	const lastSeenVersion = localStorage.getItem(LAST_SEEN_STORAGE_KEY) || currentVersion;

	const [badgeCount, setBadgeCount] = useState(Number(currentVersion)-Number(lastSeenVersion));
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const isOpen = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		localStorage.setItem(LAST_SEEN_STORAGE_KEY, currentVersion);
		setBadgeCount(0);
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	React.useEffect(() => {
		if (!localStorage.getItem(LAST_SEEN_STORAGE_KEY)) {
			localStorage.setItem(LAST_SEEN_STORAGE_KEY, currentVersion);
		}
	}, [currentVersion]);

	return (
		<Box>
			<Tooltip title={"What's New"} placement={"right"}>
				<IconButton onClick={handleClick} sx={{ color: "#fff", fontSize: 26 }}>
					<Badge
						color={"error"}
						anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
						badgeContent={badgeCount ?? undefined}
					>
						<NewReleasesIcon />
					</Badge>
				</IconButton>
			</Tooltip>
			<Popover
				open={isOpen}
				anchorEl={anchorEl}
				onClose={handleClose}
				TransitionComponent={Fade}
				anchorOrigin={{
					vertical: 'center',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				<Box sx={{ p: 2, width: "500px", maxWidth: "500px" }}>
					<Typography variant={"h6"}>
						🥳 What's New
					</Typography>
					<Typography variant={"caption"}>{format(new Date("2024-06-10"), "P")}</Typography>
					<ul>
						<li>When automatic labeling of GSAC tickets is enabled for an employee, tickets where the employee is Escalation Engineer will also be labeled with "ooo-ie" label.</li>
					</ul>
					<Typography variant={"caption"}>{format(new Date("2023-10-04"), "P")}</Typography>
					<ul>
						<li>Your user menu will now have a quick access to your profile settings. The same profile settings dialog is also available if you click on any name in the calendar. Make sure that OOO labels management function is enabled for your profile if you rely on it - this is NOT enabled by default. Read more about it <Link href={"https://hello.atlassian.net/wiki/spaces/BE/pages/744040509/Automatic+labeling+of+owned+tickets+as+OOO+for+employees+not+available+in+Vega+Calendar"} target="_blank">here</Link>.</li>
					</ul>
					<Typography variant={"caption"}>{format(new Date("2023-08-31"), "P")}</Typography>
					<p>This release includes changes to workday events display.</p>
					<ul>
						<li>The lower threshold for events to be displayed was lowered to 3 months in the past (from 1 month in the past).</li>
						<li>The restriction of at least 7 hours duration for events to be displayed has been removed. Events with lower than 7 hours will be loosely mapped throughout an employee's work day.</li>
						<li>Workday events that span multiple weeks will be connected over the weekend to form a single contiguous event.</li>
					</ul>
					<Box m={2} />
					<Link href={Const.URL_CHANGELOG} target="_blank">See full changelog</Link>
				</Box>
			</Popover>
		</Box>
	);

}